import { useEffect } from 'react';

import {
  adTrackingParams,
  DisplayAdsBiddingScript,
} from '@xing-com/crate-ads-display-ads';
import { useAds } from '@xing-com/crate-ads-native-ads';
import { AdTypes } from '@xing-com/crate-common-graphql-types';
import { Redirect, redirectTo, useLocation } from '@xing-com/crate-core-router';
import { SearchBar } from '@xing-com/crate-dream-machine-search-bar';
import { useLoginState } from '@xing-com/crate-hooks-use-login-state';
import { Head } from '@xing-com/crate-startpage-logged-out-head';
import { JobsSection } from '@xing-com/crate-startpage-logged-out-jobs-section-package';
import { NativeAppsSection } from '@xing-com/crate-startpage-logged-out-native-apps-section';
import { SeoSection } from '@xing-com/crate-startpage-logged-out-seo-section';
import { NoSSR } from '@xing-com/crate-utils-no-ssr';
import {
  SUPPORTED_AND_DEPRECATED_LOCALES,
  XingletLoader,
} from '@xing-com/crate-xinglet';
import type { Locale } from '@xing-com/crate-xinglet';
import { useRuntimeConfig } from '@xing-com/crate-xinglet/internal';
import { useExperiment } from '@xing-com/hub';
import {
  Container as LayoutPrimitivesContainer,
  Section,
} from '@xing-com/layout-primitives';

import { AdSection } from './ad-section';
import { CompaniesSection } from './companies-section';
import { DeepDiveSection } from './deep-dive-section';
import { RecruitersSection } from './recruiters-section';
import { RegisterSection } from './register-section';
import { SearchInspirationSection } from './search-inspiration-section';
import {
  StyledJobsSection,
  StyledSwimpSection,
} from './startpage-logged-out.styles';
import { useTrackPageView, useTrackClickStoreButton } from './tracking';

const getRootLanguage = (pathname: string): Locale | undefined => {
  return SUPPORTED_AND_DEPRECATED_LOCALES.find((locale) => {
    return [`/${locale}`, `/${locale}/`].includes(pathname);
  });
};

const getHiddenLocaleRedirectTarget = (
  pathname: string,
  search: string
): string => {
  const targetPath = pathname.endsWith('/') ? '/en/' : '/en';

  return `${targetPath}${search}`;
};

export const StartPageLoggedOut: React.FC = () => {
  const { visibleLocales } = useRuntimeConfig();
  const trackClickStoreButton = useTrackClickStoreButton();
  const { pathname = '', search = '' } = useLocation();
  const trackPageView = useTrackPageView();
  const rootLanguage = getRootLanguage(pathname);
  const isVisibleRootLanguage =
    rootLanguage !== undefined && visibleLocales.includes(rootLanguage);
  const isHiddenRootLanguage =
    rootLanguage !== undefined && !isVisibleRootLanguage;

  const swimpExperimentVariant = useExperiment('ABACUS-523');
  const isSwimpExperiment =
    swimpExperimentVariant === 'B' || swimpExperimentVariant === 'C';

  const isAdsExperiment = useExperiment('ABACUS-574') === 'B';
  const { ads, isCompleted, isSkipped } = useAds({
    adCount: 2,
    adTypes: [AdTypes.VideoAd, AdTypes.WebsiteAd, AdTypes.LeadAd],
    channel: 'startpage_web_logged_out',
    isLoggedOut: true,
    skipQuery: !isAdsExperiment,
  });

  useEffect(() => {
    if (!isHiddenRootLanguage) {
      trackPageView();
    }
  }, [isHiddenRootLanguage, trackPageView]);

  useEffect(() => {
    if (!rootLanguage || !isVisibleRootLanguage) {
      return;
    }

    const now = new Date();
    const oneYearFromNowDate = new Date(now.setFullYear(now.getFullYear() + 1));

    window.document.cookie = `language=${rootLanguage}; domain=.xing.com; expires=${oneYearFromNowDate.toUTCString()}`;
    window.document.cookie = `language=${rootLanguage}; expires=${oneYearFromNowDate.toUTCString()}`;
  }, [isVisibleRootLanguage, rootLanguage]);

  if (isHiddenRootLanguage) {
    redirectTo(getHiddenLocaleRedirectTarget(pathname, search), 301);
  }

  return (
    <>
      <DisplayAdsBiddingScript trackingParams={adTrackingParams.Startpage} />

      <Head page="startPage" />
      {isSwimpExperiment ? (
        <StyledSwimpSection noBlockPadding noPagePadding>
          <XingletLoader name="@xing-com/crate-startpage-logged-out-swimp-section" />
        </StyledSwimpSection>
      ) : (
        <SearchBar isStartPage withKeywords />
      )}
      <StyledJobsSection noBlockPadding $isSwimpExperiment={isSwimpExperiment}>
        <JobsSection />
      </StyledJobsSection>
      <Section noBlockPadding>
        <LayoutPrimitivesContainer variant="expanded">
          <NoSSR>
            <AdSection
              placement="top"
              ads={ads}
              isCompleted={isCompleted}
              isAdExperiment={isAdsExperiment}
              isSkipped={isSkipped}
            />
          </NoSSR>

          <SearchInspirationSection />
        </LayoutPrimitivesContainer>
      </Section>
      <CompaniesSection />
      <Section noBlockPadding>
        <LayoutPrimitivesContainer variant="expanded">
          <DeepDiveSection />
        </LayoutPrimitivesContainer>
      </Section>
      <RecruitersSection />
      <Section noBlockPadding>
        <LayoutPrimitivesContainer variant="expanded">
          <NoSSR>
            <AdSection
              placement="bottom"
              ads={ads}
              isCompleted={isCompleted}
              isAdExperiment={isAdsExperiment}
              isSkipped={isSkipped}
            />
          </NoSSR>
          <NativeAppsSection trackClickStoreButton={trackClickStoreButton} />
          <SeoSection />
          <RegisterSection />
        </LayoutPrimitivesContainer>
      </Section>
    </>
  );
};

const StartPageLoggedInRedirect: React.FC = () => {
  return (
    <>
      <Redirect to="/jobs/find" noThrow />
      <XingletLoader name="@xing-com/crate-xing-entrypoint" loadingSkeleton />
    </>
  );
};

export const StartPage: React.FC = () => {
  const { isLoggedOut } = useLoginState();
  return isLoggedOut ? <StartPageLoggedOut /> : <StartPageLoggedInRedirect />;
};
